import { useEffect, useRef, useState } from 'react'
import type { SyncPreview } from '../../../../../convex/lib/sync_preview'
import { previewRequests } from './sync-preview-request'

type Props = { reservationId: string; runId: string; revision: number;
  onPreview: (input: { nativePropertyId: number }) => Promise<SyncPreview> }
type ViewState = { key: string; ticket?: { key: string; sequence: number }; busy?: boolean; result?: SyncPreview; error?: string }
const blockers: Record<string, string> = {
  CRM_VERSION_INVALID: 'La versión de disponibilidad de Bitrix no es válida.',
  CRM_FUTURE_VERSION: 'Bitrix tiene una versión de disponibilidad posterior a la del portal.',
  SYNC_PREVIEW_REMOTE_CLAIM_CONFLICT: 'Bitrix conserva el vínculo de otra reserva en esta vivienda.',
  SYNC_PREVIEW_CLAIM_CONFLICT: 'Otra reserva está bloqueando esta vivienda.',
  SYNC_PREVIEW_PROPERTY_CLOSED: 'La vivienda figura como vendida o retirada en Bitrix.',
  CRM_DEAL_NOT_OPEN: 'La negociación está cerrada.',
  CRM_DEAL_STAGE_CONFLICT: 'La etapa de la negociación es incompatible con esta reserva.',
  CRM_DEAL_LINK_CONFLICT: 'La negociación está vinculada a otra vivienda o reserva.',
  CRM_DEAL_CHANGED: 'El responsable o la categoría de la negociación ha cambiado.',
  CRM_DEAL_CLOSURE_REVIEW: 'El cierre de la negociación requiere revisión.',
  CRM_RECONCILIATION_REQUIRED: 'Falta revisar la reserva, los contratos o los pagos.',
}
const labels: Record<string, string> = {
  stageId: 'Etapa', parentId1058: 'Vivienda vinculada',
  UF_CRM_9_A1_ACTIVE_RESERVATION: 'Reserva que bloquea la vivienda', UF_CRM_9_A1_SYNC_VERSION: 'Versión de disponibilidad',
  UF_CRM_DEAL_A1_RESERVATION_ID: 'Reserva vinculada', UF_CRM_DEAL_A1_RESERVED_AT: 'Fecha de reserva',
  UF_CRM_DEAL_A1_RESERVATION_SIGNED_AT: 'Firma de reserva', UF_CRM_DEAL_A1_SALE_SIGNED_AT: 'Firma de compraventa',
  UF_CRM_DEAL_A1_NOTARY_AT: 'Fecha de escritura',
}
export function SyncPreviewResult({ result }: { result: SyncPreview }) {
  const date = new Date(result.observedAt).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })
  return <div className="mt-4 space-y-3" role="status">
    <p className="text-sm">Consulta de prueba del {date} (Madrid). {result.status === 'blocked' ? 'Hay conflictos que requieren revisión.' : 'Comparación completada.'}</p>
    {result.blockers.length > 0 && <ul className="list-inside list-disc text-sm text-amber-900">{result.blockers.map(code => <li key={code}>{blockers[code] ?? 'Esta prueba necesita revisión.'}</li>)}</ul>}
    {[{ label: 'Vivienda', row: result.property }, { label: 'Negociación', row: result.deal }].map(({ label, row }) => {
      return <section key={label} className="rounded border p-3"><h4 className="font-medium">{label} #{row.id}</h4>
        <ul className="mt-2 space-y-2 text-sm">{row.fields.map(field => <li key={field.field} className="break-words">
          <strong>{labels[field.field] ?? field.field}</strong>: {String(field.before ?? 'Vacío')} → {String(field.after ?? 'Vacío')}
          {' · '}{field.changed ? 'Diferente' : 'Coincide'}</li>)}</ul>
        {!row.fields.length && <p className="text-sm">Sin propuesta por un conflicto.</p>}</section>
    })}
    <p className="text-sm">Reserva en Bitrix: proyección sin verificar. No se ha comprobado si existe ni si coincide.</p>
    <dl className="text-sm"><dt>Referencia prevista</dt><dd>{String(result.reservation.fields.title ?? 'Sin referencia')}</dd>
      <dt>Negociación y vivienda previstas</dt><dd>#{String(result.reservation.fields.parentId2 ?? '')} · #{String(result.reservation.fields.parentId1058 ?? '')}</dd></dl>
    <p className="text-xs text-muted-foreground">Es una comparación puntual. Repite la consulta después de cualquier cambio. No acredita que la sincronización esté lista para uso comercial.</p>
  </div>
}
export function ReservationSyncPreview({ reservationId, runId, revision, onPreview }: Props) {
  const [nativeId, setNativeId] = useState(''), [state, setState] = useState<ViewState>({ key: '' })
  const requests = useRef(previewRequests())
  const key = JSON.stringify([reservationId, runId, revision, nativeId])
  requests.current.select(key)
  useEffect(() => () => requests.current.invalidate(), [])
  const visible = state.key === key && state.ticket && requests.current.current(state.ticket) ? state : undefined
  async function inspect() {
    const id = /^[1-9][0-9]*$/.test(nativeId) ? Number(nativeId) : NaN
    if (!Number.isSafeInteger(id)) return
    const ticket = requests.current.start()
    setState({ key, ticket, busy: true })
    try {
      const result = await onPreview({ nativePropertyId: id })
      if (requests.current.current(ticket)) setState({ key, ticket, result })
    } catch {
      if (requests.current.current(ticket)) setState({ key, ticket, error: 'No se pudo comprobar esta prueba. Revisa sus vínculos, permisos y estado actual; no se ha aplicado ningún cambio.' })
    }
  }
  return <section className="mx-auto mt-5 max-w-5xl rounded-xl border bg-background p-5" aria-label="Comparación de sincronización ficticia">
    <h3 className="font-semibold">Comparar con Bitrix · Solo pruebas</h3>
    <p className="mt-2 text-sm">Solo lectura. Consulta una vivienda y una negociación registradas en esta misma prueba. No guarda cambios, envía mensajes ni activa la sincronización.</p>
    <label className="mt-3 block text-sm">Número de la vivienda de prueba en Bitrix
      <input className="mt-2 w-full rounded border p-2" inputMode="numeric" value={nativeId} onChange={e => setNativeId(e.target.value)} /></label>
    <button className="mt-3 rounded border px-4 py-2 text-sm disabled:opacity-50" disabled={!/^[1-9][0-9]*$/.test(nativeId) || !Number.isSafeInteger(Number(nativeId))}
      onClick={() => { void inspect() }}>{visible?.busy ? 'Volver a consultar' : 'Consultar sin cambiar datos'}</button>
    {visible?.busy && <p role="status" className="mt-3 text-sm">Comprobando los registros ficticios…</p>}
    {visible?.error && <p role="alert" className="mt-3 text-sm text-red-800">{visible.error}</p>}
    {visible?.result && <SyncPreviewResult result={visible.result} />}
  </section>
}
